import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../utils/cn";

import { textVariants } from "./text";

const alertVariants = cva(
    cn(
        textVariants({ variant: "body" }),
        "group/alert relative grid w-full gap-0.5 rounded-none border px-2.5 py-2 text-left has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4"
    ),
    {
        variants: {
            variant: {
                default:
                    "bg-surface-sunken border-line-base text-foreground-base",
                destructive:
                    "border-status-error-300 bg-status-error-000 text-error-base *:data-[slot=alert-description]:text-status-error-500",
                warning:
                    "border-status-warning-300 bg-status-warning-000 text-warning-base *:data-[slot=alert-description]:text-status-warning-500",
                info: "border-status-info-300 bg-status-info-000 text-info-base *:data-[slot=alert-description]:text-status-info-500",
                // Soft-ember brand-attention surface. Use when a callout needs to
                // carry brand voice (FundingSourceDetail "last active source"
                // warning) — louder than `default` but not signaling an error
                // (those use `destructive`).
                accent: "border-accent-inverse bg-accent-inverse text-foreground-base"
            }
        },
        defaultVariants: {
            variant: "default"
        }
    }
);

function Alert({
    className,
    variant,
    ...props
}: React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof alertVariants>): React.ReactElement {
    return (
        <div
            data-slot="alert"
            role="alert"
            className={cn(alertVariants({ variant }), className)}
            {...props}
        />
    );
}

function AlertTitle({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
    return (
        <div
            data-slot="alert-title"
            className={cn(
                textVariants({ variant: "body" }),
                "group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground-base",
                className
            )}
            {...props}
        />
    );
}

function AlertDescription({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
    return (
        <div
            data-slot="alert-description"
            className={cn(
                textVariants({ variant: "body" }),
                "text-balance text-foreground-neutral md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground-base [&_p:not(:last-child)]:mb-4",
                className
            )}
            {...props}
        />
    );
}

function AlertAction({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
    return (
        <div
            data-slot="alert-action"
            className={cn("absolute top-2 right-2", className)}
            {...props}
        />
    );
}

export { Alert, AlertTitle, AlertDescription, AlertAction, alertVariants };
